// Phần bổ sung cho Báo cáo Marketing (Trực page · Telesale · Sale cơ sở):
// chọn nhanh kỳ, dải tỷ lệ chuyển đổi có so sánh kỳ trước, bảng nhân sự sắp xếp được, tốc độ phản hồi & SLA, xuất Excel.
import { useMemo, useState } from 'react';
import { Avatar, Input, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Info, Search } from 'lucide-react';
import * as XLSX from 'xlsx';
import type { MarketingReportStaff } from 'api/DashboardStatistics';
import { RULES, fmtD, fmtFull, fmtMoney, fmtRate, ratio, type Range, type Snapshot } from 'pages/TrucPageDashbroad/insights';
import { Delta } from 'pages/TrucPageDashbroad/ui';

const num = (v?: number | null) => Math.round(Number(v) || 0).toLocaleString('vi-VN');

/** Ô tỷ lệ tô màu theo trung bình đội: đỏ khi thấp hơn nhiều, xanh khi cao hơn nhiều */
const RateCell = ({ v, avg, suffix }: { v: number | null; avg: number | null; suffix?: React.ReactNode }) => {
  if (v === null || !Number.isFinite(v)) return <span className="text-slate-300">—</span>;
  const tone = avg && v < avg * RULES.lagFactor ? 'font-semibold text-red-500' : avg && v >= avg * 1.2 ? 'font-semibold text-emerald-600' : 'text-slate-700';
  return <span className={tone}>{fmtRate(v)}{suffix}</span>;
};

const NameCell = ({ name, avatar, color }: { name: string; avatar?: string | null; color: string }) => (
  <span className="flex items-center gap-2">
    <Avatar size={28} src={avatar || undefined} style={{ background: color, flexShrink: 0 }}>{name.trim().split(/\s+/).pop()?.[0]}</Avatar>
    <span className="truncate font-semibold text-slate-700">{name}</span>
  </span>
);

const Card = ({ title, info, extra, children }: { title: React.ReactNode; info?: string; extra?: React.ReactNode; children: React.ReactNode }) => (
  <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <h3 className="flex items-center gap-2 text-lg font-bold text-slate-800">
        {title}
        {info && <Tooltip title={info}><Info className="h-4 w-4 text-slate-400" /></Tooltip>}
      </h3>
      {extra}
    </div>
    {children}
  </div>
);

const SearchBox = ({ value, onChange, total }: { value: string; onChange: (v: string) => void; total: number }) => (
  <Input allowClear prefix={<Search size={14} className="text-slate-400" />} placeholder={`Tìm trong ${total} nhân sự`} value={value} onChange={e => onChange(e.target.value)} className="w-full sm:w-60" />
);

const useSearch = <T extends { name: string }>(rows: T[]) => {
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const k = q.trim().toLowerCase();
    return k ? rows.filter(r => r.name.toLowerCase().includes(k)) : rows;
  }, [rows, q]);
  return { q, setQ, shown };
};

// ============================== Dải tỷ lệ chuyển đổi ==============================

export interface RateItem { label: string; value: number | null; prev: number | null; hint?: string; kind?: 'rate' | 'minutes' | 'money' | 'count'; goodWhenUp?: boolean }

export function RateStrip({ items, prevLabel }: { items: RateItem[]; prevLabel: string }) {
  const show = (it: RateItem) => (it.value === null ? '—'
    : it.kind === 'minutes' ? `${it.value.toFixed(1).replace('.', ',')} phút`
      : it.kind === 'money' ? fmtMoney(it.value)
        : it.kind === 'count' ? num(it.value)
          : fmtRate(it.value));
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-center justify-between text-xs text-slate-400">
        <span className="font-semibold uppercase tracking-wide">Tỷ lệ chuyển đổi & hiệu quả</span>
        <span>{prevLabel}</span>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        {items.map(it => (
          <div key={it.label} className="rounded-xl bg-slate-50 px-3 py-2.5" title={it.hint}>
            <div className="flex items-center gap-1 truncate text-xs text-slate-500">{it.label}{it.hint && <Info className="h-3 w-3 shrink-0 text-slate-400" />}</div>
            <div className="mt-0.5 flex items-baseline justify-between gap-2">
              <span className="text-xl font-bold text-slate-800">{show(it)}</span>
              <Delta cur={it.value} prev={it.prev} kind={it.kind === 'rate' || !it.kind ? 'points' : 'percent'} goodWhenUp={it.goodWhenUp ?? true} compact />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Ghi chú nhỏ cạnh số liệu KPI: mức thay đổi so với kỳ trước */
export const KpiDelta = ({ cur, prev, goodWhenUp = true, kind }: { cur: number | null | undefined; prev: number | null | undefined; goodWhenUp?: boolean; kind?: 'percent' | 'points' }) =>
  cur == null || prev == null ? null : (
    <span className="mt-1 flex items-center gap-1 text-xs text-slate-400">
      <Delta cur={cur} prev={prev} goodWhenUp={goodWhenUp} kind={kind} /> so với kỳ trước
    </span>
  );

// ============================== Trực page ==============================

interface PageRow {
  key: number; name: string; avatar: string | null;
  chats: number; leads: number; rate: number | null; prevLeads: number | null;
  processed: number | null; responseMin: number | null; sla: number | null; slaPass: boolean | null;
}

/** Bảng nhân sự Trực page: gộp số khách (page-performance) với tốc độ phản hồi & SLA (marketing-campaign-report) */
export function PageStaffTable({ cur, prev }: { cur: Snapshot; prev: Snapshot }) {
  const rows = useMemo(() => {
    const map = new Map<number, PageRow>();
    const get = (id: number, name: string, avatar: string | null) => {
      let r = map.get(id);
      if (!r) { r = { key: id, name, avatar, chats: 0, leads: 0, rate: null, prevLeads: null, processed: null, responseMin: null, sla: null, slaPass: null }; map.set(id, r); }
      return r;
    };
    (cur.page?.staff_customer_stats || []).forEach(s => {
      const r = get(s.staff_id, s.staff_name, s.staff_avatar);
      r.chats = s.channel_customer_count; r.leads = s.customer_count; r.rate = ratio(s.customer_count, s.channel_customer_count);
    });
    (prev.page?.staff_customer_stats || []).forEach(s => { const r = map.get(s.staff_id); if (r) r.prevLeads = s.customer_count; });
    (cur.mkt?.staff_response_ranking || []).forEach((s: MarketingReportStaff) => {
      const r = get(s.staff_id, s.name, s.avatar);
      r.processed = s.processed_messages; r.responseMin = s.avg_response_minutes; r.sla = s.sla_rate_percent; r.slaPass = s.status === 'pass';
    });
    return Array.from(map.values());
  }, [cur, prev]);
  const { q, setQ, shown } = useSearch(rows);
  const chats = rows.reduce((s, r) => s + r.chats, 0);
  const avgRate = ratio(rows.reduce((s, r) => s + r.leads, 0), chats);
  const slaMinutes = cur.mkt?.period?.sla_minutes;

  const columns: ColumnsType<PageRow> = [
    { title: 'Nhân viên', dataIndex: 'name', fixed: 'left', width: 220, render: (v, r) => <NameCell name={v} avatar={r.avatar} color="#3b82f6" /> },
    { title: 'Khách nhắn tin', dataIndex: 'chats', width: 130, align: 'right', sorter: (a, b) => a.chats - b.chats, render: num },
    {
      title: 'Khách hàng mới', dataIndex: 'leads', width: 150, align: 'right', defaultSortOrder: 'descend', sorter: (a, b) => a.leads - b.leads,
      render: (v, r) => <span><b className="text-slate-800">{num(v)}</b>{r.prevLeads !== null && <span className="ml-1.5"><Delta cur={v} prev={r.prevLeads} compact /></span>}</span>,
    },
    { title: 'Tỷ lệ lấy thông tin', dataIndex: 'rate', width: 150, align: 'right', sorter: (a, b) => (a.rate ?? -1) - (b.rate ?? -1), render: v => <RateCell v={v} avg={avgRate} /> },
    { title: 'Tin đã xử lý', dataIndex: 'processed', width: 120, align: 'right', sorter: (a, b) => (a.processed ?? -1) - (b.processed ?? -1), render: v => (v === null ? <span className="text-slate-300">—</span> : num(v)) },
    {
      title: <Tooltip title="Thời gian trung bình từ lúc khách nhắn đến lúc nhân viên trả lời (trong giờ làm việc)">Phản hồi TB</Tooltip>, dataIndex: 'responseMin', width: 120, align: 'right',
      sorter: (a, b) => (a.responseMin ?? Infinity) - (b.responseMin ?? Infinity),
      render: v => (v === null ? <span className="text-slate-300">—</span> : <span className={slaMinutes && v > slaMinutes ? 'font-semibold text-red-500' : ''}>{Number(v).toFixed(1).replace('.', ',')} phút</span>),
    },
    {
      title: <Tooltip title={`Tỷ lệ tin nhắn được trả lời trong ${slaMinutes ?? '—'} phút`}>Đạt SLA</Tooltip>, dataIndex: 'sla', width: 130, align: 'right',
      sorter: (a, b) => (a.sla ?? -1) - (b.sla ?? -1),
      render: (v, r) => (v === null ? <span className="text-slate-300">—</span> : <Tag className="m-0" color={r.slaPass ? 'green' : 'red'}>{fmtRate(Number(v))}</Tag>),
    },
  ];

  return (
    <Card
      title="Bảng hiệu suất nhân viên Trực page"
      info={`Tỷ lệ tô đỏ: thấp hơn trung bình đội (${fmtRate(avgRate)}) từ 30% trở lên; tô xanh: cao hơn từ 20%. Phản hồi tô đỏ khi chậm hơn SLA ${slaMinutes ?? ''} phút.`}
      extra={<SearchBox value={q} onChange={setQ} total={rows.length} />}
    >
      <Table<PageRow> size="middle" rowKey="key" columns={columns} dataSource={shown} scroll={{ x: 1020 }} pagination={shown.length > 10 ? { pageSize: 10, showSizeChanger: false } : false} locale={{ emptyText: 'Chưa có dữ liệu' }} />
    </Card>
  );
}

/** Tốc độ phản hồi & SLA của Trực page */
export function ResponseSlaCard({ cur, prev }: { cur: Snapshot; prev: Snapshot }) {
  const s = cur.mkt?.summary;
  const ps = prev.mkt?.summary;
  const staff = [...(cur.mkt?.staff_response_ranking || [])].sort((a, b) => a.avg_response_minutes - b.avg_response_minutes);
  const period = cur.mkt?.period;
  const fail = staff.filter(x => x.status !== 'pass');
  return (
    <Card title="Tốc độ phản hồi & SLA" info={period ? `Giờ làm việc ${period.working_hours}, SLA ${period.sla_minutes} phút.` : undefined}>
      {!s ? <div className="py-10 text-center font-medium text-slate-400">Chưa có dữ liệu</div> : (
        <>
          <div className="mb-4 grid grid-cols-3 gap-3">
            <div className="rounded-xl bg-slate-50 p-3">
              <div className="text-xs text-slate-500">Phản hồi trung bình</div>
              <div className="text-2xl font-bold text-slate-800">{Number(s.avg_response_minutes).toFixed(1).replace('.', ',')}<span className="text-sm font-medium text-slate-500"> phút</span></div>
              {ps && <Delta cur={s.avg_response_minutes} prev={ps.avg_response_minutes} goodWhenUp={false} />}
            </div>
            <div className="rounded-xl bg-slate-50 p-3">
              <div className="text-xs text-slate-500">Tỷ lệ đạt SLA</div>
              <div className={`text-2xl font-bold ${s.overall_sla_rate >= 80 ? 'text-emerald-600' : s.overall_sla_rate >= 60 ? 'text-amber-600' : 'text-red-500'}`}>{fmtRate(Number(s.overall_sla_rate))}</div>
              {ps && <Delta cur={s.overall_sla_rate} prev={ps.overall_sla_rate} kind="points" />}
            </div>
            <div className="rounded-xl bg-slate-50 p-3">
              <div className="text-xs text-slate-500">Nhân viên đạt SLA</div>
              <div className="text-2xl font-bold text-slate-800">{s.staff_achieved_sla || '—'}</div>
              <div className="text-xs text-slate-400">{num(s.total_messages)} tin nhắn</div>
            </div>
          </div>
          {fail.length > 0 ? (
            <>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Chưa đạt SLA</div>
              <ul className="space-y-1.5">
                {fail.slice(0, 6).map(x => (
                  <li key={x.staff_id} className="flex items-center gap-2 rounded-lg bg-red-50/60 px-3 py-1.5 text-[13px]">
                    <span className="min-w-0 flex-1 truncate font-medium text-slate-700">{x.name}</span>
                    <span className="text-slate-500">{x.time_display || `${x.avg_response_minutes} phút`}</span>
                    <span className="w-14 text-right font-semibold text-red-500">{fmtRate(Number(x.sla_rate_percent))}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">Tất cả nhân viên đều đạt SLA trong kỳ.</div>}
        </>
      )}
    </Card>
  );
}

// ============================== Telesale ==============================

interface TeleRow { key: number; name: string; avatar: string | null; rank: number; booked: number; showup: number; closed: number; showRate: number; closeRate: number; prevClosed: number | null; prevBooked: number | null }

export function TeleStaffTable({ cur, prev }: { cur: Snapshot; prev: Snapshot }) {
  const rows = useMemo<TeleRow[]>(() => {
    const prevMap = new Map((prev.tele?.rankings || []).map(r => [r.staff_id, r]));
    return (cur.tele?.rankings || []).map(r => ({
      key: r.staff_id, name: r.name, avatar: r.avatar, rank: r.rank, booked: Number(r.booked) || 0, showup: Number(r.showup) || 0, closed: Number(r.closed) || 0,
      showRate: Number(r.showup_rate) || 0, closeRate: Number(r.close_rate) || 0,
      prevClosed: prevMap.has(r.staff_id) ? Number(prevMap.get(r.staff_id)!.closed) || 0 : null,
      prevBooked: prevMap.has(r.staff_id) ? Number(prevMap.get(r.staff_id)!.booked) || 0 : null,
    }));
  }, [cur, prev]);
  const { q, setQ, shown } = useSearch(rows);
  const avgShow = cur.tele ? Number(cur.tele.summary.showup_rate) : null;
  const avgClose = cur.tele ? Number(cur.tele.summary.close_rate) : null;
  const total = { booked: rows.reduce((s, r) => s + r.booked, 0), showup: rows.reduce((s, r) => s + r.showup, 0), closed: rows.reduce((s, r) => s + r.closed, 0) };

  const columns: ColumnsType<TeleRow> = [
    { title: 'Hạng', dataIndex: 'rank', width: 70, align: 'center', sorter: (a, b) => a.rank - b.rank, render: v => <span className="font-mono font-semibold text-slate-600">#{v}</span> },
    { title: 'Nhân viên', dataIndex: 'name', fixed: 'left', width: 220, render: (v, r) => <NameCell name={v} avatar={r.avatar} color="#7F77DD" /> },
    {
      title: 'Lịch hẹn đã đặt', dataIndex: 'booked', width: 150, align: 'right', defaultSortOrder: 'descend', sorter: (a, b) => a.booked - b.booked,
      render: (v, r) => <span><b className="text-slate-800">{num(v)}</b>{r.prevBooked !== null && <span className="ml-1.5"><Delta cur={v} prev={r.prevBooked} compact /></span>}</span>,
    },
    { title: 'Khách đến', dataIndex: 'showup', width: 110, align: 'right', sorter: (a, b) => a.showup - b.showup, render: num },
    { title: 'Tỷ lệ đến', dataIndex: 'showRate', width: 120, align: 'right', sorter: (a, b) => a.showRate - b.showRate, render: v => <RateCell v={v} avg={avgShow} /> },
    {
      title: 'Khách chốt', dataIndex: 'closed', width: 130, align: 'right', sorter: (a, b) => a.closed - b.closed,
      render: (v, r) => <span><b className="text-emerald-600">{num(v)}</b>{r.prevClosed !== null && <span className="ml-1.5"><Delta cur={v} prev={r.prevClosed} compact /></span>}</span>,
    },
    { title: 'Tỷ lệ chốt', dataIndex: 'closeRate', width: 120, align: 'right', sorter: (a, b) => a.closeRate - b.closeRate, render: v => <RateCell v={v} avg={avgClose} /> },
  ];

  return (
    <Card
      title="Bảng xếp hạng nhân viên Telesale"
      info={`Bấm tiêu đề cột để sắp xếp. Tỷ lệ tô đỏ: thấp hơn trung bình đội từ 30% trở lên; tô xanh: cao hơn từ 20%. Mũi tên: so với kỳ trước.`}
      extra={<SearchBox value={q} onChange={setQ} total={rows.length} />}
    >
      <Table<TeleRow>
        size="middle" rowKey="key" columns={columns} dataSource={shown} scroll={{ x: 960 }} pagination={shown.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
        locale={{ emptyText: 'Chưa có dữ liệu' }}
        summary={() => rows.length > 1 ? (
          <Table.Summary fixed>
            <Table.Summary.Row className="bg-slate-50 font-semibold">
              <Table.Summary.Cell index={0} />
              <Table.Summary.Cell index={1}>Cả đội</Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">{num(total.booked)}</Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">{num(total.showup)}</Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="right">{fmtRate(avgShow)}</Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="right">{num(total.closed)}</Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="right">{fmtRate(avgClose)}</Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        ) : null}
      />
    </Card>
  );
}

// ============================== Sale cơ sở ==============================

interface SaleRow { key: number; name: string; avatar: string | null; showup: number; closed: number; rate: number; revenue: number; aov: number; share: number | null; prevRevenue: number | null }

export function SaleStaffTable({ cur, prev }: { cur: Snapshot; prev: Snapshot }) {
  const total = cur.sale ? Number(cur.sale.summary.total_revenue) : 0;
  const rows = useMemo<SaleRow[]>(() => {
    const prevMap = new Map((prev.sale?.rankings || []).map(r => [r.staff_id, r]));
    return (cur.sale?.rankings || []).map(r => ({
      key: r.staff_id, name: r.name, avatar: r.avatar, showup: Number(r.showup) || 0, closed: Number(r.closed) || 0, rate: Number(r.rate) || 0,
      revenue: Number(r.revenue) || 0, aov: Number(r.aov) || 0, share: ratio(Number(r.revenue) || 0, total),
      prevRevenue: prevMap.has(r.staff_id) ? Number(prevMap.get(r.staff_id)!.revenue) || 0 : null,
    }));
  }, [cur, prev, total]);
  const { q, setQ, shown } = useSearch(rows);
  const avgRate = cur.sale ? Number(cur.sale.summary.close_rate) : null;
  const ranked = useMemo(() => [...rows].sort((a, b) => b.revenue - a.revenue).map(r => r.key), [rows]);

  const columns: ColumnsType<SaleRow> = [
    {
      title: 'Hạng', key: 'rank', width: 70, align: 'center',
      render: (_, r) => { const i = ranked.indexOf(r.key); return i < 3 ? <span className="text-lg">{['🥇', '🥈', '🥉'][i]}</span> : <span className="font-mono text-slate-500">#{i + 1}</span>; },
    },
    { title: 'Nhân viên', dataIndex: 'name', fixed: 'left', width: 220, render: (v, r) => <NameCell name={v} avatar={r.avatar} color="#1D9E75" /> },
    { title: 'Khách tư vấn', dataIndex: 'showup', width: 120, align: 'right', sorter: (a, b) => a.showup - b.showup, render: num },
    { title: 'Khách chốt', dataIndex: 'closed', width: 110, align: 'right', sorter: (a, b) => a.closed - b.closed, render: v => <b className="text-emerald-600">{num(v)}</b> },
    { title: 'Tỷ lệ chốt', dataIndex: 'rate', width: 120, align: 'right', sorter: (a, b) => a.rate - b.rate, render: v => <RateCell v={v} avg={avgRate} /> },
    { title: 'Đơn TB', dataIndex: 'aov', width: 120, align: 'right', sorter: (a, b) => a.aov - b.aov, render: v => <span title={fmtFull(v)}>{fmtMoney(v)}</span> },
    {
      title: 'Doanh thu', dataIndex: 'revenue', width: 170, align: 'right', defaultSortOrder: 'descend', sorter: (a, b) => a.revenue - b.revenue,
      render: (v, r) => (
        <span title={fmtFull(v)}>
          <b className="text-blue-700">{fmtMoney(v)}</b>
          {r.prevRevenue !== null && <span className="ml-1.5"><Delta cur={v} prev={r.prevRevenue} compact /></span>}
        </span>
      ),
    },
    {
      title: 'Tỷ trọng', dataIndex: 'share', width: 140, sorter: (a, b) => (a.share ?? 0) - (b.share ?? 0),
      render: v => (
        <div className="flex items-center gap-2">
          <span className="block h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100"><span className="block h-full rounded-full bg-blue-500" style={{ width: `${Math.min(100, v ?? 0)}%` }} /></span>
          <span className="w-12 text-right text-xs text-slate-500">{fmtRate(v)}</span>
        </div>
      ),
    },
  ];

  return (
    <Card
      title="Bảng xếp hạng nhân viên Sale cơ sở"
      info="Hạng theo doanh thu. Bấm tiêu đề cột để sắp xếp. Tỷ lệ chốt tô đỏ khi thấp hơn trung bình đội từ 30% trở lên. Mũi tên: doanh thu so với kỳ trước."
      extra={<SearchBox value={q} onChange={setQ} total={rows.length} />}
    >
      <Table<SaleRow> size="middle" rowKey="key" columns={columns} dataSource={shown} scroll={{ x: 1080 }} pagination={shown.length > 10 ? { pageSize: 10, showSizeChanger: false } : false} locale={{ emptyText: 'Chưa có dữ liệu' }} />
    </Card>
  );
}

// ============================== Xuất Excel ==============================

export function exportReport(tab: string, cur: Snapshot, prev: Snapshot, range: Range) {
  const wb = XLSX.utils.book_new();
  const add = (name: string, rows: Record<string, unknown>[]) => XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.length ? rows : [{ '': 'Không có dữ liệu' }]), name);
  const kv = (label: string, c?: number | null, p?: number | null) => ({ 'Chỉ số': label, 'Kỳ này': c ?? '', 'Kỳ trước': p ?? '' });
  if (tab === 'page') {
    const s = cur.page?.summary; const ps = prev.page?.summary;
    add('Tong quan', [
      kv('Tin nhắn mới', s?.total_chat_customers, ps?.total_chat_customers),
      kv('Khách hàng mới', s?.total_new_customers, ps?.total_new_customers),
      kv('Lịch hẹn', s?.total_appointments, ps?.total_appointments),
      kv('Phản hồi TB (phút)', cur.mkt?.summary.avg_response_minutes, prev.mkt?.summary.avg_response_minutes),
      kv('Tỷ lệ đạt SLA (%)', cur.mkt?.summary.overall_sla_rate, prev.mkt?.summary.overall_sla_rate),
    ]);
    add('Nhan vien', (cur.page?.staff_customer_stats || []).map(x => {
      const r = cur.mkt?.staff_response_ranking?.find(y => y.staff_id === x.staff_id);
      return { 'Nhân viên': x.staff_name, 'Khách nhắn tin': x.channel_customer_count, 'Khách hàng mới': x.customer_count, 'Tỷ lệ (%)': +(ratio(x.customer_count, x.channel_customer_count) ?? 0).toFixed(1), 'Phản hồi TB (phút)': r?.avg_response_minutes ?? '', 'Đạt SLA (%)': r?.sla_rate_percent ?? '' };
    }));
    add('Fanpage', (cur.page?.customer_by_channels || []).map(x => ({ Fanpage: x.name, 'Số khách': x.count, 'Kỳ trước': prev.page?.customer_by_channels?.find(y => y.name === x.name)?.count ?? '' })));
    add('The khach hang', (cur.page?.customer_by_tags || []).map(x => ({ 'Thẻ': x.name, 'Số khách': x.count })));
    add('Ket qua cuoc goi', (cur.page?.call_result_summary || []).map(x => ({ 'Kết quả': x.name, 'Số khách': x.count, 'Tỷ lệ (%)': x.ratio })));
    add('Doanh thu nen tang', (cur.page?.revenue_by_platforms || []).map(x => ({ 'Nền tảng': x.name, 'Doanh thu': x.revenue })));
  } else if (tab === 'telesale') {
    const s = cur.tele?.summary; const ps = prev.tele?.summary;
    add('Tong quan', [
      kv('Lịch hẹn đã đặt', s?.total_booked, ps?.total_booked), kv('Khách đến', s?.total_showup, ps?.total_showup),
      kv('Tỷ lệ đến (%)', s?.showup_rate, ps?.showup_rate), kv('Khách chốt', s?.total_closed, ps?.total_closed),
      kv('Tỷ lệ chốt (%)', s?.close_rate, ps?.close_rate), kv('Doanh thu', cur.tele?.revenue?.current_period, cur.tele?.revenue?.prev_period),
    ]);
    add('Nhan vien', (cur.tele?.rankings || []).map(x => ({ 'Hạng': x.rank, 'Nhân viên': x.name, 'Lịch đặt': x.booked, 'Khách đến': x.showup, 'Tỷ lệ đến (%)': x.showup_rate, 'Khách chốt': x.closed, 'Tỷ lệ chốt (%)': x.close_rate })));
    add('Ket qua cuoc goi', (cur.tele?.call_result_summary || []).map(x => ({ 'Kết quả': x.name, 'Số khách': x.total, 'Tỷ lệ (%)': x.ratio })));
    add('The cham soc', (cur.tele?.tag_summary || []).map(x => ({ 'Mã': x.code, 'Thẻ': x.tag, 'Số khách': x.total })));
    add('Dich vu', (cur.tele?.service_rankings || []).map(x => ({ 'Mã': x.code, 'Dịch vụ': x.name, 'Lịch đặt': x.booked, 'Chốt': x.closed, 'Tỷ lệ (%)': x.ratio })));
  } else {
    const s = cur.sale?.summary; const ps = prev.sale?.summary;
    add('Tong quan', [
      kv('Khách đến cơ sở', s?.total_showup, ps?.total_showup), kv('Khách chốt', s?.total_closed, ps?.total_closed),
      kv('Tỷ lệ chốt (%)', s?.close_rate, ps?.close_rate), kv('Doanh thu', s?.total_revenue, ps?.total_revenue), kv('Đơn trung bình', s?.avg_order_value, ps?.avg_order_value),
    ]);
    add('Nhan vien', (cur.sale?.rankings || []).map(x => ({ 'Nhân viên': x.name, 'Khách tư vấn': x.showup, 'Khách chốt': x.closed, 'Tỷ lệ chốt (%)': x.rate, 'Đơn TB': x.aov, 'Doanh thu': x.revenue })));
    add('Trang thai', (cur.sale?.status_summary || []).map(x => ({ 'Trạng thái': x.name, 'Thẻ': x.tag ?? '', 'Số khách': x.total })));
    add('Dich vu', (cur.sale?.service_summary || []).map(x => ({ 'Mã': x.code, 'Dịch vụ': x.name, 'Số khách': x.total, 'Doanh thu': x.revenue })));
  }
  const name = tab === 'page' ? 'truc-page' : tab === 'telesale' ? 'telesale' : 'sale-co-so';
  XLSX.writeFile(wb, `bao-cao-${name}_${fmtD(range.start)}_${fmtD(range.end)}.xlsx`);
}

/** Sắp xếp theo số thứ tự đứng đầu tên/mã (S1, 2. …) để phễu đi đúng thứ tự giai đoạn */
export const stageOrder = (label: string, fallback: number) => {
  const m = String(label).match(/\d+(?:\.\d+)?/); // "S6.1" phải đứng giữa S6 và S7
  return m ? Number(m[0]) : 1000 + fallback;
};
